import axios from "axios";

type TokenCache = {
  token: string;
  expiresAt: number;
};

type LoginApiResponse = {
  status: boolean;
  msgs: string[];
  result: {
    token: string;
    message: string;
    expire: number;
  };
};

const SAFETY_MS = 30_000;

export const API_TIMEOUT_MS = 25_000;

let cache: TokenCache | null = null;
let pending: Promise<TokenCache> | null = null;

function ttlMs(): number {
  const seconds = Number(process.env.NEXT_API_TIME_VALIDATE) || 180;
  return seconds * 1000;
}

function readJwtExpiration(token: string): number | null {
  try {
    const payload = JSON.parse(Buffer.from(token.split(".")[1], "base64").toString("utf8"));
    return typeof payload?.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function loginApi(): Promise<TokenCache> {
  const baseUrl = process.env.NEXT_API_LAMBDA_URL;

  if (!baseUrl) {
    throw new Error("NEXT_API_LAMBDA_URL não configurada");
  }

  const { data } = await axios.post<LoginApiResponse>(
    `${baseUrl}/Auth/LoginAPI`,
    {
      userName: process.env.NEXT_API_USERNAME,
      password: process.env.NEXT_API_PASSWORD,
      system: process.env.NEXT_API_SYSTEM
    },
    {
      headers: { "Content-Type": "application/json" },
      timeout: API_TIMEOUT_MS
    }
  );

  if (!data?.status || !data?.result?.token) {
    throw new Error(`Falha ao autenticar na API: ${data?.msgs?.join(", ")}`);
  }

  const token = data.result.token;
  const configuredExpiration = Date.now() + ttlMs();
  const jwtExpiration = readJwtExpiration(token);

  return {
    token,
    expiresAt: jwtExpiration ? Math.min(configuredExpiration, jwtExpiration) : configuredExpiration
  };
}

export async function getApiToken(): Promise<string> {
  if (typeof window !== "undefined") {
    throw new Error("getApiToken só pode ser usado no servidor");
  }

  if (cache && cache.expiresAt - SAFETY_MS > Date.now()) {
    return cache.token;
  }

  if (!pending) {
    pending = loginApi()
      .then((fresh) => {
        cache = fresh;
        return fresh;
      })
      .finally(() => {
        pending = null;
      });
  }

  return (await pending).token;
}

export function invalidateApiToken(rejectedToken?: string): void {
  if (!rejectedToken || cache?.token === rejectedToken) {
    cache = null;
  }
}
